import { Box, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { DBlock, DPaginator, DTextField, FilterIcon } from '../../index';
import {
  CTableWrapper,
  ICustomTableWrapperProps
} from '../c-table-wrapper/c-table-wrapper';

export interface ICMinifiedTableProps<T> {
    // Heading shown above the filter row (e.g. "Ventas").
    title?: string;
    // Filters the caller applies beyond the search box (scope, hidden record
    // kinds, fixed sort). The minified table shows no column filters, so these
    // are listed in a tooltip on a filter icon next to the title.
    appliedFilters?: string[];
    // General free-text filter (single search box). Column filters are NOT shown
    // in the minified variant — this is the only filter input.
    filterValue: string;
    onFilterChange: (value: string) => void;
    filterLabel?: string;
    filterHelperText?: string;
    // Pagination — wire straight from usePaginator.
    skip: number;
    take: number;
    setSkip: (skip: number) => void;
    count?: number;
    // Table — same column/collapse shape as CTableWrapper. Any per-column
    // `filter` is stripped here, so callers can hand over their full columns.
    items: ICustomTableWrapperProps<T>['items'];
    data: T[];
    loading?: boolean;
    onRowDoubleClick?: (item: T) => void;
    collapse?: ICustomTableWrapperProps<T>['collapse'];
    contextItems?: ICustomTableWrapperProps<T>['contextItems'];
    defaultOpenCollapse?: boolean;
}

/**
 * Compact, embeddable table block: a heading, a single general search filter,
 * the paginator, and the original CTableWrapper — with column filters removed.
 * Used to surface a paginated list inside another page (e.g. the per-product
 * activity tables on the product page).
 */
export function CMinifiedTable<T>({
    title,
    appliedFilters,
    filterValue,
    onFilterChange,
    filterLabel = 'Filtro',
    filterHelperText,
    skip,
    take,
    setSkip,
    count,
    items,
    data,
    loading = false,
    onRowDoubleClick,
    collapse,
    contextItems,
    defaultOpenCollapse,
}: ICMinifiedTableProps<T>) {
    // Minified shows only the general filter, so drop any per-column filter UI.
    const itemsWithoutColumnFilters = items.map(({ filter, ...rest }) => rest);

    return (
        <DBlock>
            {title ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        {title}
                    </Typography>
                    {appliedFilters && appliedFilters.length > 0 ? (
                        <Tooltip
                            title={
                                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                    {appliedFilters.map((f) => (
                                        <li key={f}>{f}</li>
                                    ))}
                                </Box>
                            }
                        >
                            <FilterIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
                        </Tooltip>
                    ) : null}
                </Box>
            ) : null}
            <Box
                sx={{
                    display: 'flex',
                    // Stack the filter above the paginator on medium and below
                    // so they stop competing for one cramped row.
                    flexDirection: { xs: 'column', lg: 'row' },
                    alignItems: { xs: 'stretch', lg: 'center' },
                    mb: 2,
                    gap: 2,
                }}
            >
                <Box sx={{ flex: 1 }}>
                    <DTextField
                        label={filterLabel}
                        helperText={filterHelperText}
                        value={filterValue}
                        onChange={onFilterChange}
                        hideHelperTextBelowLg
                    />
                </Box>
                <DPaginator
                    skip={skip}
                    take={take}
                    setSkip={setSkip}
                    count={count}
                    disableGutters
                />
            </Box>
            <CTableWrapper
                items={itemsWithoutColumnFilters}
                data={data}
                loading={loading}
                onRowDoubleClick={onRowDoubleClick}
                collapse={collapse}
                contextItems={contextItems}
                defaultOpenCollapse={defaultOpenCollapse}
            />
        </DBlock>
    );
}
